import { computed, inject, signal, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { pipe, switchMap, tap } from 'rxjs';

import { ToastService } from '@ideals/components/toast';
import { LoggerService } from '@ideals/core/logger';
import { downloadFile } from '@ideals/utils/download-file';
import { rxMethod } from '@ideals/utils/rx-method';
import { tapResponse } from '@ideals/utils/tap-response';

import { Article, Country, HelpCenterConfig, HelpCenterSection, Project, SearchArticle, Video } from '../models';
import { CountryCodeService, DEFAULT_COUNTRY_CODE } from '../services/country-code';
import { HelpCenterService } from '../services/help-center';
import { getArticleForSection } from '../utils/get-article-for-section';

export const ALL_ARTICLES_FILE_NAME = 'IdealsManual.html';

@Service({ autoProvided: false })
export class HelpCenterStore {
  readonly #countryCodeService = inject(CountryCodeService);
  readonly #helpCenterService = inject(HelpCenterService);
  readonly #loggerService = inject(LoggerService);
  readonly #toastService = inject(ToastService);
  readonly #translateService = inject(TranslateService);

  readonly #loadArticles = rxMethod<void>(
    pipe(
      tap(() => this.articlesLoading.set(true)),
      switchMap(() => this.#helpCenterService.loadArticles(this.#translateService.getCurrentLang() as string).pipe(
        tapResponse({
          next: (allArticles) => {
            this.allArticles.set(allArticles);
            this.articlesLoading.set(false);
          },
          error: (error) => {
            this.#requestError(error);
            this.articlesLoading.set(false);
          },
        }),
      )),
    ),
  );

  readonly #loadArticlesIndex = rxMethod<void>(
    pipe(
      tap(() => this.articlesIndexLoading.set(true)),
      switchMap(() => this.#helpCenterService.loadArticlesIndex(this.#translateService.getCurrentLang() as string).pipe(
        tapResponse({
          next: (articlesIndex) => {
            this.articlesIndex.set(articlesIndex);
            this.articlesIndexLoading.set(false);
          },
          error: (error) => {
            this.#requestError(error);
            this.articlesIndexLoading.set(false);
          },
        }),
      )),
    ),
  );

  readonly #loadCountries = rxMethod<void>(
    pipe(
      tap(() => this.countriesLoading.set(true)),
      switchMap(() => this.#helpCenterService.loadCountries(this.#translateService.getCurrentLang() as string).pipe(
        tapResponse({
          next: (countries) => {
            this.countries.set(countries);
            this.countriesLoading.set(false);
          },
          error: (error) => {
            this.#requestError(error);
            this.countriesLoading.set(false);
          },
        }),
      )),
    ),
  );

  readonly #loadSelectedCountryCode = rxMethod<void>(
    pipe(
      tap(() => this.selectedCountryCodeLoading.set(true)),
      switchMap(() => this.#countryCodeService.getCountryCode$().pipe(
        tapResponse({
          next: (selectedCountryCode) => {
            this.selectedCountryCode.set(selectedCountryCode);
            this.selectedCountryCodeLoading.set(false);
          },
          error: (error) => {
            this.#requestError(error);
            this.selectedCountryCodeLoading.set(false);
          },
        }),
      )),
    ),
  );

  readonly #loadVideos = rxMethod<void>(
    pipe(
      tap(() => this.videosLoading.set(true)),
      switchMap(() => this.#helpCenterService.loadVideos().pipe(
        tapResponse({
          next: (videos) => {
            this.videos.set(videos);
            this.videosLoading.set(false);
          },
          error: (error) => {
            this.#requestError(error);
            this.videosLoading.set(false);
          },
        }),
      )),
    ),
  );

  readonly allArticles = signal<Article[]>([]);
  readonly articlesIndex = signal<SearchArticle[]>([]);
  readonly articlesIndexLoading = signal(false);
  readonly articlesLoading = signal(false);
  readonly countries = signal<Country[]>([]);
  readonly countriesLoading = signal(false);
  readonly loadedForLanguage = signal('');
  readonly project = signal<Project | undefined>(undefined);
  readonly section = signal<HelpCenterSection>('');
  readonly selectedCountryCode = signal<string>(DEFAULT_COUNTRY_CODE);
  readonly selectedCountryCodeLoading = signal(false);
  readonly videos = signal<Video[]>([]);
  readonly videosLoading = signal(false);

  readonly articles = computed(() => getArticleForSection(this.allArticles(), this.section()));
  readonly contactsLoading = computed(() => this.countriesLoading() || this.selectedCountryCodeLoading());
  readonly searchLoading = computed(() => this.articlesLoading() || this.articlesIndexLoading());
  readonly selectedCountry = computed(() => {
    return this.countries().find(({ countryCode }) => countryCode === this.selectedCountryCode());
  });

  readonly downloadArticles = rxMethod<void>(
    pipe(
      switchMap(() => this.#helpCenterService.loadAllArticlesFile(this.#translateService.getCurrentLang() as string).pipe(
        tapResponse({
          next: (file) => downloadFile(file, ALL_ARTICLES_FILE_NAME),
          error: (error) => this.#requestError(error),
        }),
      )),
    ),
  );

  loadData(): void {
    if (this.#translateService.getCurrentLang() === this.loadedForLanguage()) {
      return;
    }

    this.#loadVideos();
    this.#loadArticles();
    this.#loadCountries();
    this.#loadArticlesIndex();
    this.#loadSelectedCountryCode();
    this.loadedForLanguage.set(this.#translateService.getCurrentLang() as string);
  }

  setConfig(config: HelpCenterConfig | { section: HelpCenterSection; }): void {
    this.project.set('project' in config ? config.project : undefined);
    this.section.set(config.section);
  }

  setSelectedCountryCode(selectedCountryCode: string): void {
    this.selectedCountryCode.set(selectedCountryCode);
    this.#countryCodeService.saveCountryCode(selectedCountryCode);
  }

  #requestError(error: unknown): void {
    this.#toastService.error('common.Error.Something_went_wrong');
    this.#loggerService.handleHttpError(error);
  }
}
